/**
 * SOLID Review
 * S — Strong. One use case: lift the cup — the decider cleared before the call, the table moved whatever the server says.
 * O — Strong. The round trip runs it; a change of rule is the Domain's, of sequence the round trip's.
 * L — Strong. Implements its use-case role and the round trip's three strategies exactly.
 * I — Strong. `execute`, and the three strategies the round trip asks for.
 * D — Strong. The decisions and server ports and the round trip, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPropsClientTableInput } from '../../domain/ports/schemas/ClientTable';
import type { ICommandNothing, ICommandRoundMoveAnswered, IPayloadRoundMove } from '../../domain/ports/schemas/TableMoves';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { TServerRoundTripToTheEnd, TAnsweredVoid } from '../shared/ServerRoundTrip';
import type { IConfigDepTable } from './IConfigDepTable';

/**
 * The decider lifts (FE1's liftCup). The plan clears the server's decider BEFORE the call, so the table moves whatever the
 * server says (`runToTheEnd`), and a refused lift is the answer.
 */
export class LiftCupUseCase implements IUseCase<ICommandNothing, TPortOutcome>, TServerRoundTripToTheEnd<IPropsClientTableInput, ICommandNothing, IPayloadRoundMove, TAnsweredVoid> {
  constructor(private readonly deps: IConfigDepTable) {}

  execute(ctx: IContextRequest, command: ICommandNothing): Promise<TPortOutcome> {
    return this.deps.trip.runToTheEnd(ctx, command, this);
  }

  plan(ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return this.deps.decisions.planLift(ctx, input, command);
  }

  send(ctx: IContextRequest, plan: IPayloadRoundMove) {
    return this.deps.rounds.liftCup(ctx, plan);
  }

  answer(ctx: IContextRequest, input: IPropsClientTableInput, plan: IPayloadRoundMove) {
    const lifted: ICommandRoundMoveAnswered = { request: plan };
    return this.deps.decisions.liftAnswered(ctx, input, lifted);
  }
}
